/**
 * Accessibility Utils
 * 
 * This file contains utilities for improving accessibility throughout the application,
 * including standardized ARIA attributes, focus management, and screen reader support.
 */

/**
 * Types of ARIA live regions
 */
export type AriaLive = 'off' | 'polite' | 'assertive';

/**
 * Focus trap for modals and dialogs
 * Keeps focus within the specified element
 */
export class FocusTrap {
  private element: HTMLElement;
  private focusableElements: HTMLElement[];
  private firstFocusableElement: HTMLElement | null = null;
  private lastFocusableElement: HTMLElement | null = null;
  private previousActiveElement: HTMLElement | null = null;

  constructor(element:  HTMLElement) {
    this.element = element;
    this.focusableElements = this.getFocusableElements();
    this.updateFocusableElements();
    
    // Store the element that had focus before the trap was activated
    this.previousActiveElement = document.activeElement as HTMLElement;
  }

  /**
   * Get all focusable elements within the container
   */
  private getFocusableElements(): HTMLElement[] {
    const focusableSelectors = [
      'button:not([disabled])',
      '[href]:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      'details:not([disabled])',
      'summary:not(:disabled)'
    ];

    const elements = this.element.querySelectorAll(focusableSelectors.join(','));
    return Array.from(elements) as HTMLElement[];
  }

  /**
   * Update the first and last focusable elements
   */
  private updateFocusableElements(): void {
    this.focusableElements = this.getFocusableElements();
    this.firstFocusableElement = this.focusableElements[0] || null;
    this.lastFocusableElement = 
      this.focusableElements[this.focusableElements.length - 1] || null;
  }

  /**
   * Activate the focus trap
   */
  activate(): void {
    // Store the current active element
    this.previousActiveElement = document.activeElement as HTMLElement;
    
    // Focus the first element
    setTimeout(() => {
      if (this.firstFocusableElement) {
        this.firstFocusableElement.focus();
      }
    }, 0);

    // Add event listener for tab key
    this.element.addEventListener('keydown', this.handleKeyDown);
  }

  /**
   * Deactivate the focus trap
   */
  deactivate(): void {
    // Remove event listener
    this.element.removeEventListener('keydown', this.handleKeyDown);
    
    // Restore focus to previous element
    if (this.previousActiveElement) {
      setTimeout(() => {
        this.previousActiveElement?.focus();
      }, 0);
    }
  }

  /**
   * Handle keydown events, primarily tab navigation
   */
  private handleKeyDown = (e:  KeyboardEvent): void => {
    // If not Tab key, do nothing
    if (e.key !== 'Tab') return;

    // Update focusable elements in case DOM changed
    this.updateFocusableElements();

    // If no focusable elements, do nothing
    if (!this.firstFocusableElement || !this.lastFocusableElement) return;
    
    // Handle forward tab navigation
    if (!e.shiftKey && document.activeElement === this.lastFocusableElement) {
      e.preventDefault();
      this.firstFocusableElement.focus();
    } 
    // Handle backward tab navigation
    else if (e.shiftKey && document.activeElement === this.firstFocusableElement) {
      e.preventDefault();
      this.lastFocusableElement.focus();
    }
  };
}

/**
 * Add event listeners for keyboard navigation on interactive elements
 */
export function makeKeyboardAccessible(element: HTMLElement, callback: () => void): () => void {
  const handleKeyDown = (e:  KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  element.addEventListener('keydown', handleKeyDown);
  
  // Return a cleanup function
  return () => {
    element.removeEventListener('keydown', handleKeyDown);
  };
}

/**
 * Generate consistent ARIA attributes for common UI patterns
 */
export const ariaAttributes = {
  /**
   * Attributes for tabs
   */
  tabs: {
    tablist: (label: string) => ({
      role: 'tablist',
      'aria-label': label,
    }),
    tab: (selected: boolean, controls: string) => ({
      role: 'tab',
      'aria-selected': selected,
      'aria-controls': controls,
      tabIndex: selected ? 0 : -1,
    }),
    tabpanel: (id: string, labelledby: string) => ({
      role: 'tabpanel',
      id,
      'aria-labelledby': labelledby,
      tabIndex: 0,
    }),
  },

  /**
   * Attributes for modals
   */
  modal: {
    dialog: (labelledby: string, describedby?: string) => ({
      role: 'dialog',
      'aria-modal': true,
      'aria-labelledby': labelledby,
      ...(describedby && { 'aria-describedby': describedby }),
    }),
    closeButton: (label = 'Close') => ({
      'aria-label': label,
    }),
  },

  /**
   * Attributes for accordions
   */
  accordion: {
    button: (expanded: boolean, controls: string) => ({
      'aria-expanded': expanded,
      'aria-controls': controls,
    }),
    panel: (id: string, hidden: boolean) => ({
      id,
      role: 'region',
      'aria-hidden': hidden,
    }),
  },

  /**
   * Attributes for combobox/dropdown
   */
  dropdown: {
    button: (expanded: boolean, controls: string) => ({
      'aria-expanded': expanded,
      'aria-haspopup': true,
      'aria-controls': controls,
    }),
    listbox: (id: string, labelledby: string) => ({
      id,
      role: 'listbox',
      'aria-labelledby': labelledby,
    }),
    option: (id: string, selected: boolean) => ({
      id,
      role: 'option',
      'aria-selected': selected,
    }),
  },

  /**
   * Attributes for live regions
   */
  liveRegion: (politeness: AriaLive = 'polite') => ({
    'aria-live': politeness,
    'aria-atomic': true,
  }),
};

/**
 * Hook for managing focus in modals
 */
export function useFocusTrap(enabled:  boolean): {
  ref: React.RefCallback<HTMLElement>
} {
  const focusTrapRef = React.useRef<FocusTrap | null>(null);
  const nodeRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!nodeRef.current) return;

    if (enabled) {
      focusTrapRef.current = new FocusTrap(nodeRef.current);
      focusTrapRef.current.activate();
    } else {
      focusTrapRef.current?.deactivate();
      focusTrapRef.current = null;
    }

    return () => {
      focusTrapRef.current?.deactivate();
    };
  }, [enabled]);

  return {
    ref: React.useCallback((node:  HTMLElement | null) => {
      nodeRef.current = node;
    }, []),
  };
}

// Needed for the React hooks above
import React from 'react';
